/**
 * Universal helper function to handle Prisma Decimal values
 * Converts various decimal formats to JavaScript numbers
 * 
 * @param decimal - The decimal value in any format (string, number, or Prisma Decimal object)
 * @returns The numeric value or null if invalid
 */
export function decimalToNumber(decimal: any): number | null {
  if (!decimal) return null;
  
  // Already a number
  if (typeof decimal === 'number') return decimal;
  
  // String format (most common from API)
  if (typeof decimal === 'string') return parseFloat(decimal);
  
  // Handle raw Prisma Decimal object (if somehow not serialized)
  if (decimal.d && Array.isArray(decimal.d)) {
    const sign = decimal.s || 1;
    const digits = decimal.d.join('');
    const exponent = decimal.e || 0;
    
    // Correct formula for Prisma Decimal reconstruction
    const num = parseInt(digits) * Math.pow(10, exponent - digits.length + 1);
    return sign < 0 ? -num : num;
  }
  
  return null;
}

/**
 * Convert USD price to points (1 USD = 100 points)
 * 
 * @param usdPrice - The USD price value
 * @returns Points value (USD * 100, rounded)
 */
export function usdToPoints(usdPrice: number | null): number | null {
  if (usdPrice === null || usdPrice === undefined) return null;
  return Math.round(usdPrice * 100);
}

/**
 * Convert any decimal format to points
 * Combines decimalToNumber and usdToPoints for convenience
 * 
 * @param decimal - The decimal value in any format
 * @returns Points value or null
 */
export function decimalToPoints(decimal: any): number | null {
  const numericValue = decimalToNumber(decimal);
  return usdToPoints(numericValue);
}